import type { CMSAssetItem, CMSAssetType } from '../../types';
import { useToast } from '@workramp/ui';
import { useState } from 'react';
import { APP_CONSTANTS, I18n } from '~/common/explicit-exports';
import { CMSCopyLinkModal } from '../CMSCopyLinkModal/CMSCopyLinkModal';
import { useShareLink } from '../../api/shareLink';
import { CMSShareMenu } from '../CMSShareMenu/CMSShareMenu';

type CMSShareButtonProps = {
  asset:
    | CMSAssetItem
    | {
        id: string;
        assetType: CMSAssetType;
        allowExternalSharing: boolean;
        allowInternalSharing: boolean;
      };
  iconButton?: boolean;
  label?: string;
  onCopy?: () => void;
  tooltipText?: string;
  url?: string;
};

export const CMSShareButton = ({ asset, iconButton, label, onCopy, tooltipText, url }: CMSShareButtonProps) => {
  const { open: openToast } = useToast();

  const shareLinkMutation = useShareLink();

  const TRANSLATED_COPY_LINK_MODAL_HEADER = I18n.t('js.admin.cms.copy_link_modal.header');
  const TRANSLATED_COPY_LINK_MODAL_BODY = I18n.t('js.admin.cms.copy_link_modal.body');
  const CONFIRMATION_TOAST = I18n.t('js.admin.cms.copy_link_modal.confirmation_toast');
  const ERROR_TOAST = I18n.t('js.admin.cms.copy_link_modal.error_toast');

  const [shareExternalLinkModalOpen, setShareExternalLinkModalOpen] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const { allowExternalSharing, allowInternalSharing } = asset;

  return (
    <>
      <CMSShareMenu
        label={label}
        iconButton={iconButton}
        assetType={asset.assetType}
        toolTipText={tooltipText}
        isInternalSharingEnabled={allowInternalSharing}
        isExternalSharingEnabled={allowExternalSharing}
        externalSharingHandler={() => {
          shareLinkMutation.mutate(
            { id: asset.id, assetType: asset.assetType },
            {
              onSuccess: (data) => {
                setShareExternalLinkModalOpen(true);
                setShareLink(data.link);
              },
              onError: () => {
                openToast({ children: <span>{ERROR_TOAST}</span> });
              },
            },
          );
        }}
        internalSharingHandler={async () => {
          // navigator.clipboard is only available in https
          if (asset.assetType === 'LINK' && onCopy) onCopy();
          if (!navigator.clipboard) return;
          const currentURL = new URL(window.location.href);
          let copiedURL = '';
          if (asset.assetType === APP_CONSTANTS.CMS_ASSET_TYPES.FILE) {
            copiedURL = new URL(`/cms_files/view?id=${asset.id}`, currentURL.origin).toString();
          } else if (url) {
            copiedURL = url;
          }
          await navigator.clipboard.writeText(copiedURL.toString());
          openToast({ children: <span>{CONFIRMATION_TOAST}</span> });
        }}
      />
      <CMSCopyLinkModal
        header={TRANSLATED_COPY_LINK_MODAL_HEADER}
        body={TRANSLATED_COPY_LINK_MODAL_BODY}
        link={shareLink}
        isOpen={shareExternalLinkModalOpen}
        onClose={() => {
          setShareExternalLinkModalOpen(false);
        }}
      />
    </>
  );
};
